<script setup>
import axios from 'axios';
import { defineProps, defineEmits, ref, onMounted, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from '@/composables/use-toast.js';
import USDCLogo  from '@/assets/images/usdc.png';
import StripeLogo  from '@/assets/images/stripe.png';
import PortexLogo from '@/assets/images/avatar/orb_avatar.png';
import StripePayment from './StripePayment.vue';
import CountryStep from './CountryStep.vue';
import Checkbox from '@/components/checkbox.vue';
import USDCPayment from './USDCPayment.vue';
import { useStorage } from '@/composables/use-storage';
import { getActivePaymentMethods } from '@/services/paymentMethodsService';
const { hasFlag, getUserInfo } = useStorage();

const router = useRouter();

const userInfo = ref(getUserInfo());


const { addToast } = useToast();


const props = defineProps({
    showCheckoutModal: {
        type: Boolean,
        required: true
    },
    buyActive: {
        type: Object,
        required: true
    },
    isBid: {
        type: Boolean,
        required: false,
        default: false
    },
    isFinishSale: {
        type: Boolean,
        required: false,
        default: false
    }
});

const emit = defineEmits(['update:showCheckoutModal', 'purchase-completed', 'payment-success', 'finish']);

const buyNowClicked = ref(false);

const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    const numPrice = Number(price);
    return '$ ' + numPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const calculateTotalPrice = ref(0)

const toggleCloseButton = () => {
    if(buyNowClicked.value) {
        addToast('Please wait for the transaction to complete', 'warning');
        return;
    }
    emit('update:showCheckoutModal', false);
    buyNowClicked.value = false;
    showStripePayment.value = false;
};

const handleBuyNow = async () => {
    showUSDCPayment.value = true;
};

const createPayment = async (amountPayment, tx) => {
    if(tx){
        payments.value.push({
            id: availablePayments.value.find(p => p.name === 'wallet')?.id,
            method: 'wallet',
            amount: amountPayment
        })
    }
    if(props.isBid){
        await confirmBid(amountPayment, tx);
    } else if(props.isFinishSale) {
        await finishSale( tx);
    } else {
        await createSale( tx);
    }
}

const confirmBid = async (amountPayment, tx) => {
    const values = {
        note: 'Payment for bid',
        receipt_address: props.buyActive.receipt_address,
        tx_hash: tx?.transactionHash || 'void_tx',
        bid_amount: amountPayment,
        payments: payments.value
    }
    const response = await axios.post(`/api/bids/${props.buyActive.id}/confirm-payment`, values);
    if (response?.data?.bid?.status === 'PAID') {
        emit('payment-success');
        addToast('Payment successful', 'success');
    } else {
        addToast('Purchase didn\'t work, please try again later', 'error');
    }
}

const createSale = async (tx) => {
    const values = {
        listing_id: listingId.value,
        buyer_id: userInfo.value.id || null,
        final_price: payments.value.reduce((acc, payment) => acc + payment.amount, 0),
        currency: 'USD',
        tx_hash: tx?.transactionHash || 'void_tx',
        payments: payments.value
    }

    const response = await axios.post('/api/sales', values);
    if (response?.data?.paid || response?.data?.tx_hash === 'void_tx') {
        window.location.href = `/checkout-confirmation?sale_id=${response.data.id}`;
    } else {
        alert('Purchase didn\'t work, please try again later')
    }
}

const finishSale = async (tx) => {
    const values = {
        sale_id: props.buyActive.id,
        final_price: payments.value.reduce((acc, payment) => acc + payment.amount, 0),
        currency: 'USD',
        tx_hash: tx?.transactionHash || 'void_tx',
        payments: payments.value,
        receipt_address: props.buyActive.receipt_address
    }

    const response = await axios.post('/api/sales/finish-payment', values);
    if (response?.data?.paid || response?.data?.tx_hash === 'void_tx') {
        emit('payment-success');
    } else {
        alert('Purchase didn\'t work, please try again later')
    }
}

const handlePayWithStripe = () => {
    showStripePayment.value = true;
    payments.value.push({
        id: availablePayments.value.find(p => p.name === 'stripe')?.id,
        method: 'stripe',
        amount: calculateTotalPrice.value
    })
}

const handleConnectStripe = () => {
    router.push('/stripe/connect');
}

const handlePaymentCredits = async () => {
    const total = payments.value.reduce((acc, payment) => acc + payment.amount, 0);
    await createPayment(total.toString(), null);
}

const availablePayments = ref([]);

const paymentMethods = ref([]);

const showStripePayment = ref(false);
const showUSDCPayment = ref(false);

const goBackToMain = () => {
    showStripePayment.value = false;
    showUSDCPayment.value = false;
    payments.value.pop();
};

const payments = ref([]);

const useCredits = ref(false);
const creditsBalance = ref(0);
const creditsUsed = ref(0);
const isModalInitialized = ref(false);
const amount = ref(props.isBid || props.isFinishSale ? props.buyActive.amount : props.buyActive.price);
const listingId = ref(props.isBid || props.isFinishSale ? props.buyActive.listing_id : props.buyActive.id);

const handleUseCredits = async () => {
    if(props.buyActive === null) return;
    let price = Number.parseFloat(amount.value) + Number.parseFloat(feeAmout.value);
    let used = 0;
    const credits = parseFloat(creditsBalance.value);
    if(useCredits.value && credits >= price) {
        used = price;
        price = 0;
    }
    if(useCredits.value && credits < price) {
        used = credits;
        price = price - credits;
    }
    creditsUsed.value = used;
    
    payments.value = payments.value.filter(p => p.method !== 'credits');
    
    if(used > 0) {
        payments.value.push({
            id: availablePayments.value.find(p => p.name === 'credits')?.id,
            method: 'credits',
            amount: used
        });
    }
    
    calculateTotalPrice.value = price;
}

const fetchCredits = async () => {
    const response = await axios.get('/api/users/credits')
    creditsBalance.value = response.data;
}

const userCountry = ref({
    country: userInfo.value.country,
    postalCode: userInfo.value.postal_code
});

const handleUserCountry = ( value ) => {
    userCountry.value = value;
}

const canUseCredits = computed(() => {
    return props.buyActive.accept_credits && creditsBalance.value > 0 && !props.buyActive.applicant_id;
})

const handlePaymentSuccess = () => {
    emit('payment-success');
}

const handleFinish = () => {
    emit('finish');
}

watch(useCredits, async () => {
    if (isModalInitialized.value) {
        await handleUseCredits();
    }
})

const feeAmout = ref(0);

onMounted(async () => {
    availablePayments.value = await getActivePaymentMethods();
    const sellerStripeAccount = props.isBid || props.isFinishSale 
        ? props.buyActive.seller_account_id 
        : props.buyActive.datasets[0].dataset.seller.stripe_account_id;
    if (props.buyActive?.payment_methods?.length > 0 && props.buyActive.payment_methods?.filter(method => method === availablePayments.value.find(p => p.name === 'wallet')?.id).length > 0) {
        paymentMethods.value.push({
            id: availablePayments.value.find(p => p.name === 'wallet')?.id,
            name: 'USDC',
            logo: USDCLogo,
            description: 'Save fees, requires a wallet.',
            payWith: 'Pay with USDC',
            clickAction: handleBuyNow,
        });
    }
    if (hasFlag('stripe-payment')) {
        if (sellerStripeAccount && props.buyActive.payment_methods?.filter(method => method === availablePayments.value.find(p => p.name === 'stripe')?.id).length > 0) {
            paymentMethods.value.push({
                id: availablePayments.value.find(p => p.name === 'stripe')?.id,
                name: 'Stripe',
                logo: StripeLogo,
                description: 'Pay with credit card',
                payWith: 'Pay with Stripe',
                clickAction: handlePayWithStripe,
            });
        }
    }
    if (calculateTotalPrice.value > 0 && useCredits.value) {
        if (props.buyActive.payment_methods?.filter(method => method === availablePayments.value.find(p => p.name === 'credits')?.id).length > 0) {
            paymentMethods.value.push({
                id: availablePayments.value.find(p => p.name === 'credits')?.id,
                name: 'Credits',
                logo: PortexLogo,
                description: 'Use credits',
                payWith: 'Pay with Credits',
                clickAction: handlePaymentCredits,
            });
        }
    }

    await fetchCredits();
    await handleUseCredits();
    
    feeAmout.value  = props.buyActive.fee > 0 ? (Number(amount.value) * Number(props.buyActive.fee)) / 100 : 0;
    setTimeout(() => {
        isModalInitialized.value = true;
    }, 100);
    
})
</script>

<template>
    <Teleport to="body">
        <div v-if="props.showCheckoutModal" 
             class="fixed inset-0 z-50 overflow-hidden flex justify-center items-center bg-echoes-background-black/50 backdrop-blur-md" data-modal-backdrop="static" >
            <div class="relative w-full max-w-2xl p-4 animate-slideIn">
                <div class="relative bg-echoes-background-gray rounded-[21px] shadow-xl min-w-[280px] animate-slideIn overflow-hidden border border-echoes-border-light">
                        <!-- Header -->
                        <div class="flex justify-between border-b border-echoes-border-light items-center px-6 py-4">
                            <div class="flex items-center gap-3">
                                <button v-if="showStripePayment || showUSDCPayment" 
                                        @click="goBackToMain"
                                        class="text-gray-400 hover:text-white rounded-lg p-1.5 inline-flex items-center transition-colors">
                                    <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <h5 class="text-xl font-semibold">
                                    {{ showStripePayment ? 'Stripe Payment' : showUSDCPayment ? 'USDC Payment' : 'Checkout' }}
                                </h5>
                            </div>
                            <button type="button"
                                    @click="toggleCloseButton"
                                    class="text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg p-1.5 inline-flex items-center transition-colors">
                                <span class="sr-only">Close modal</span>
                                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
    
                        <!-- Content with slide transition -->
                        <div class="relative overflow-hidden overflow-x-hidden max-h-[75vh]">
                            <Transition name="slide" mode="out-in">
                                <!-- Main Content -->
                                <div key="main" v-if="!showStripePayment && !showUSDCPayment" class="px-6 py-2 max-h-[calc(85vh-0px)] overflow-y-auto overflow-x-hidden">
                                    <!-- Price Breakdown -->
                                    <div class="space-y-3">
                                        <div class="flex justify-between items-center">
                                            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Data Purchase Price</p>
                                            <p class="text-sm font-semibold text-echoes-primary">
                                                {{ formatPrice(amount - feeAmout) }}
                                            </p>
                                        </div>
                                        <div class="flex justify-between items-center">
                                            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Platform Fee</p>
                                            <p class="text-sm font-semibold text-echoes-primary">
                                                {{ formatPrice(feeAmout) }}
                                            </p>
                                        </div>
                                        <div class="flex justify-between items-center bg-echoes-primary/10 rounded-lg p-2 text-sm font-medium text-gray-700 dark:text-gray-300" v-if="canUseCredits">
                                            <div>You have <span class="text-echoes-primary font-bold">{{ formatPrice(creditsBalance) }}</span> Credits</div>
                                            <div class="flex items-center gap-2">
                                                <Checkbox v-model="useCredits" />
                                                <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Use credits</p>
                                            </div>
                                        </div>
                                        <div class="flex justify-between items-center pt-3 border-t dark:border-gray-700">
                                            <p class="text-base font-semibold text-gray-900 dark:text-white">Total Price</p>
                                            <p class="text-base font-bold text-echoes-primary">
                                                {{ formatPrice(calculateTotalPrice) }}
                                            </p>
                                        </div>
                                    </div>
        
                                    <!-- Action Buttons -->
                                    <div class="mt-4 overflow-x-hidden">
                                        <div class="text-lg font-medium border-b border-gray-600/50 pb-">Select Payment Method</div>
                                        <div class="overflow-x-hidden">
                                            <Transition v-if="isModalInitialized" name="slide" mode="out-in">
                                                <div key="normal-payments" v-if="calculateTotalPrice > 0" class="justify-center items-center gap-8 my-4" :class="paymentMethods.length > 1 ? 'grid grid-cols-2' : 'flex'">
                                                    <span v-if="paymentMethods.length === 0" class="font-medium py-4 text-center">No payment methods available for this listing. Please contact <a href="mailto:hello@portexai.com" class="text-echoes-primary">hello@portexai.com</a> so we can analyze this case.</span>
                                                    <div v-for="payment in paymentMethods" :key="payment.name" 
                                                        @click="payment.clickAction" :disabled="buyNowClicked" 
                                                        class="payment-method-card" 
                                                        :class="buyNowClicked ? 'opacity-50 cursor-not-allowed': 'cursor-pointer' ">
                                                        <div class="pb-2">
                                                            <p class="text-lg">{{ payment.name }}</p>
                                                            <p class="text-xs">{{ payment.description }}</p>
                                                        </div>
                                                        <div class="flex justify-center items-center">
                                                            <img :src="payment.logo" :alt="payment.name" class="w-14">
                                                        </div>
                                                        <div class="flex justify-center">
                                                            <p class="text-xs font-medium rounded-md bg-white text-echoes-primary px-1" v-if="!buyNowClicked">{{ payment.payWith }}</p>
                                                            
                                                                <svg v-else class="animate-spin size-5" viewBox="0 0 24 24">
                                                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
                                                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                                                </svg>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div key="credits-only" v-else class="flex justify-center items-center gap-8 my-4">
                                                    <div 
                                                        @click="handlePaymentCredits()" :disabled="buyNowClicked" 
                                                        class="payment-method-card" 
                                                        :class="buyNowClicked ? 'opacity-50 cursor-not-allowed': 'cursor-pointer' ">
                                                        <div class="pb-2">
                                                            <p class="text-lg">Credits</p>
                                                            <p class="text-xs">Pay with platform credits</p>
                                                        </div>
                                                        <div class="flex justify-center items-center">
                                                            <img :src="PortexLogo" alt="Portex Logo" class="w-14">
                                                        </div>
                                                        <div class="flex justify-center">
                                                            <p class="text-xs font-medium rounded-md bg-white text-purple-600 px-1" v-if="!buyNowClicked">Pay with Credits</p>
                                                            <svg v-else class="animate-spin size-5" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Transition>
                                        </div>
                                    </div>
                                </div>
                                
                                <div key="stripe" v-else-if="showStripePayment" class="h-full max-h-[calc(85vh-150px)] overflow-y-auto overflow-x-hidden">
                                    <CountryStep v-if="userCountry.country === null" @save="handleUserCountry"/>
                                    <StripePayment @back="goBackToMain" :payments="payments" :listingId="listingId" :bidId="props.isBid ? props.buyActive.id : null" v-else/>
                                </div>
                                
                                <div key="usdc" v-else-if="showUSDCPayment" class="h-full overflow-y-auto overflow-x-hidden">
                                    <USDCPayment @back="goBackToMain" :paymentAmount="calculateTotalPrice" :buyActive="props.buyActive" :userInfo="userInfo" :creditsUsed="creditsUsed" :listingId="listingId" :isBid="props.isBid" @payment-success="handlePaymentSuccess" @finish="handleFinish" />
                                </div>
                            </Transition>
                        </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="postcss">

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.animate-slideIn {
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Slide transition styles */
.slide-enter-active,
.slide-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-x: hidden !important;
}

.slide-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.slide-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.overflow-y-auto {
    scrollbar-width: thin;
    scrollbar-color: #FE3E01 transparent;
}

.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: #FE3E01;
    border-radius: 3px;
    transition: background-color 0.3s ease;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: #7C3AED;
}

@media (max-height: 600px) {
    .max-h-\[70vh\] {
        max-height: 80vh;
    }
    
    .max-h-\[calc\(70vh-120px\)\] {
        max-height: calc(80vh - 80px);
    }
}

@media (max-height: 500px) {
    .max-h-\[70vh\] {
        max-height: 90vh;
    }
    
    .max-h-\[calc\(70vh-120px\)\] {
        max-height: calc(90vh - 60px);
    }
}

@media (max-width: 640px) {
    .max-w-xl {
        max-width: calc(100vw - 2rem);
        margin: 1rem;
    }
}

.slide-enter-active .overflow-y-auto,
.slide-leave-active .overflow-y-auto {
    scroll-behavior: smooth;
}

.payment-method-card {
    @apply border-2 border-gray-600/50 rounded-xl px-4 pt-2 pb-4 text-center hover:bg-echoes-border-light transition-all duration-200 space-y-2 shadow-sm shadow-black transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-100;
}

</style>
